import React, { FC, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { useAppDispatch } from "hooks";
import {
  getSideMenuObjectsByQueryThunk,
  resetSideMenuObjects,
  setSideMenuObjectsPageNumber
} from "app/features";
import ObjectListingCard from "components/cards/ObjectLIstingCard/ObjectListingCard";
import { buildSideMenuObjectsQueryParams } from "app/data/helpers/sideMenuObjectsQuery.helpers";

import { Skeleton } from "./Object.styled";
import { useMapState } from "components/maps/Map/v2/GoogleMap/MapStateContext";
import { useSearchParams } from "react-router-dom";
import { isEmpty } from "lodash";

interface ObjectsMapListingProps {
  drawerSelectedAccountId: string;
  sideMenuObjects?: any;
  sideMenuObjectsLoading?: any;
  totalSideMenuRecords?: any;
  resolvedAddresses?: any;
  resolvedAddressLoading?: any;
  showInactiveObjects?: boolean;
  onObjectSelect?: (uniqueId: string) => void;
}

const useOptionalMapStateObjectChange = () => {
  try {
    return useMapState()?.onObjectChange;
  } catch {
    return undefined;
  }
};

const ObjectsMapListing: FC<ObjectsMapListingProps> = ({
  drawerSelectedAccountId,
  sideMenuObjects = [],
  sideMenuObjectsLoading,
  totalSideMenuRecords = 0,
  resolvedAddresses,
  resolvedAddressLoading,
  showInactiveObjects = false,
  onObjectSelect
}) => {
  const dispatch = useAppDispatch();
  const contextOnObjectChange = useOptionalMapStateObjectChange();
  const handleObjectCardClick =
    onObjectSelect ?? contextOnObjectChange ?? (() => {});
  const [searchParams] = useSearchParams();
  const search = searchParams.get("search") || "";
  const isFetching = useRef(false);
  const previousShowInactive = useRef(showInactiveObjects);

  const loadMoreObjects = () => {
    if (sideMenuObjects.length < totalSideMenuRecords) {
      const nextPage = Math.ceil(sideMenuObjects.length / 25) + 1;

      dispatch(
        getSideMenuObjectsByQueryThunk({
          accountId: drawerSelectedAccountId,
          params: buildSideMenuObjectsQueryParams(
            search,
            nextPage,
            showInactiveObjects
          )
        })
      );

      dispatch(setSideMenuObjectsPageNumber(nextPage));
    }
  };

  const fetchSideMenuObjectsData = async (page: number = 1) => {
    isFetching.current = true;
    await dispatch(
      getSideMenuObjectsByQueryThunk({
        accountId: drawerSelectedAccountId,
        params: buildSideMenuObjectsQueryParams(
          search,
          page,
          showInactiveObjects
        )
      })
    );

    isFetching.current = false;
  };

  useEffect(() => {
    if (!isEmpty(drawerSelectedAccountId) && isFetching.current === false) {
      dispatch(resetSideMenuObjects());
      dispatch(setSideMenuObjectsPageNumber(1));
      fetchSideMenuObjectsData(1);
    }
  }, [drawerSelectedAccountId, search]);

  useEffect(() => {
    if (previousShowInactive.current === showInactiveObjects) return;
    previousShowInactive.current = showInactiveObjects;

    if (isEmpty(drawerSelectedAccountId)) return;

    dispatch(resetSideMenuObjects());
    dispatch(setSideMenuObjectsPageNumber(1));
    fetchSideMenuObjectsData(1);
  }, [showInactiveObjects, drawerSelectedAccountId]);

  return (
    <>
      {!sideMenuObjects.length && !sideMenuObjectsLoading ? (
        <Box sx={{ textAlign: "center", fontWeight: 700 }}>
          No Objects available
        </Box>
      ) : (
        <InfiniteScroll
          dataLength={sideMenuObjects.length}
          next={loadMoreObjects}
          hasMore={sideMenuObjects.length < totalSideMenuRecords}
          loader={
            <Box sx={{ textAlign: "center", p: 1.5 }}>
              <Skeleton width={"100%"} height={40} />
              <Skeleton width={"100%"} height={40} />
              <Skeleton width={"100%"} height={40} />
              <Skeleton width={"100%"} height={40} />
              <Skeleton width={"100%"} height={40} />
            </Box>
          }
          scrollThreshold={0.9}
          endMessage={
            <Box sx={{ textAlign: "center", py: 3, fontWeight: 500 }}>
              No more records to load
            </Box>
          }
          scrollableTarget="scrollableDiv"
        >
          {sideMenuObjects.map((object: any) => (
            <ObjectListingCard
              key={object.uniqueId}
              object={object}
              onObjectCardClick={handleObjectCardClick}
              resolvedAddresses={resolvedAddresses}
              resolvedAddressLoading={resolvedAddressLoading}
            />
          ))}
        </InfiniteScroll>
      )}
    </>
  );
};

export default React.memo(ObjectsMapListing);
